
import {
	IAPIPackageVariant,
	IAPIPurchasedPackageGrouped,
	IAPIJobForRecruiter,
	IAPIBuyablePackageProperty,
	GenericPackagePropertyName,
	IAPIOrganizationPreview,
	IAPIBuyablePackage
} from '@hokify/common';
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import { formatPrice } from '../helpers/price';
import { getDiscount } from '../helpers/checkout';

export default defineComponent({
	name: 'PricePackageCollapsible',
	data() {
		return {
			isBestseller:
				(this.jobPackage.buyablePackage?.buyablePackage as IAPIBuyablePackage)?.identifier ===
				'attract',
			formatPrice,
			packageIdentifier: (this.jobPackage.buyablePackage?.buyablePackage as IAPIBuyablePackage)
				?.identifier,
			// same value as isBestseller but can be toggled unlike isBestseller
			isExpanded:
				(this.jobPackage.buyablePackage?.buyablePackage as IAPIBuyablePackage)?.identifier ===
				'attract'
		};
	},
	computed: {
		calculatedDiscount(): number {
			return getDiscount(this.selectedContingent, this.jobPackage.contingentDiscounts || []);
		},
		getBuyablePackageProperties(): IAPIBuyablePackageProperty[] {
			if (this.jobPackage.buyablePackage.type === 'buyablePackageProperty') {
				return [this.jobPackage.buyablePackage.buyablePackage];
			}
			return [
				...this.jobPackage.buyablePackage.buyablePackage.buyablePackageProperties, // standard properties vom paket
				...(this.jobPackage.buyablePackage.buyablePackageProperties || []) // costumized properties wie social media reichweiter oder whatever
			];
		},
		getJobPackageRuntime() {
			const runtimeProperties = this.getRuntimeProperties(this.getBuyablePackageProperties);
			const runtime = runtimeProperties.reduce((acc, prop) => acc + (prop?.value || 0), 0);

			return runtime;
		},
		getMetaBoostPropertyValue(): number | null {
			return (
				this.getBuyablePackageProperties.find(prop => prop.propertyName === 'metaboost')?.value ||
				null
			);
		},
		hasSocialMediaPerformance(): boolean {
			return this.getBuyablePackageProperties.some(
				prop => prop.propertyName === 'socialMediaPerformance'
			);
		},
		chosen(): boolean {
			return (
				this.job?.internal?.payment?.package?._id ===
				this.jobPackage.buyablePackage?.buyablePackage?._id
			);
		},
		customButtonText(): string {
			// packageswitching
			if (this.chosen || this.showUpsellingInfo) {
				return '';
			}
			if (this.job && !this.numberOfPurchasedPackages) {
				return 'kein Paket verfügbar';
			}
			if (this.numberOfPurchasedPackages) {
				return `${this.numberOfPurchasedPackages}x verfügbar`;
			}
			return 'Inserat erstellen';
		},
		switchPackageButtonText(): string {
			if (this.chosen) {
				return 'Ausgewählt';
			}
			if (!this.numberOfPurchasedPackages) {
				return 'Paket kaufen';
			}
			return 'Paket wählen';
		},
		viablePackagesForJob(): IAPIPurchasedPackageGrouped | null {
			return (
				this.purchasedPackages?.find(
					p =>
						p.package.buyablePackage?._id === this.jobPackage.buyablePackage.buyablePackage._id &&
						(!this.filterOrganization || p.organizationId === this.filterOrganization._id)
				) || null
			);
		},
		numberOfPurchasedPackages(): number {
			if (this.job) {
				return this.viablePackagesForJob?.amount || 0;
			}
			return (
				this.purchasedPackages?.reduce(
					(acc, p) =>
						acc +
						(p.package.buyablePackage?._id === this.jobPackage.buyablePackage.buyablePackage._id
							? p.amount
							: 0),
					0
				) || 0
			);
		},
		currentPrice(): string {
			return this.formatPrice(
				this.jobPackage.price * this.selectedContingent - this.currentDiscount
			);
		},
		currentDiscount(): number {
			return this.jobPackage.price * this.selectedContingent * (this.calculatedDiscount / 100);
		},
		currentPackagePrice(): number {
			return Math.floor(this.jobPackage.price - this.currentPackageDiscount);
		},
		currentPackageDiscount(): number {
			return this.jobPackage.price * (this.calculatedDiscount / 100);
		},
		showUpsellingInfo(): boolean {
			return !!(this.job && this.job.internal.payment?.package?.identifier?.startsWith('trial_'));
		},
		subHeader(): string {
			if (this.packageIdentifier === 'impact') {
				return 'Einfach beschleunigen:';
			}
			if (this.packageIdentifier === 'attract') {
				return 'Einfach sichtbar:';
			}
			return 'Einfach loslegen:';
		},
		sanitizedJobPackageName() {
			return this.jobPackage.name.replace(/!/g, '');
		}
	},
	methods: {
		getRuntimeProperties(buyablePackageProperties: IAPIBuyablePackageProperty[]) {
			return buyablePackageProperties.filter(
				prop =>
					prop.propertyName === GenericPackagePropertyName.Runtime ||
					prop.propertyName === GenericPackagePropertyName.AdditionalRuntime
			);
		},
		handleClick() {
			if (this.$isMobile.phone) {
				this.isExpanded = !this.isExpanded;
			}
		},
		imagePath(image: string) {
			return require(`../../assets/svgs/${image}`); // eslint-disable-line
		}
	},
	props: {
		filterOrganization: { type: Object as PropType<IAPIOrganizationPreview> },
		jobPackage: { type: Object as PropType<IAPIPackageVariant>, required: true },
		buttonTextPackage: { type: String, required: false, default: 'Inserat schalten' },
		purchasedPackages: { type: Array as PropType<IAPIPurchasedPackageGrouped[]> },
		packageOnly: { type: Boolean, default: false },
		appendCustomFeatures: { type: Boolean, default: true },
		switchPackage: { type: Boolean, default: false },
		job: { type: Object as PropType<IAPIJobForRecruiter> },
		selectedContingent: { type: Number, default: 1 },
		isCompanyApp: { type: Boolean, default: false }
	}
});
